import { type FC, type ReactNode, useCallback, useEffect, useLayoutEffect, useState } from "react";
import DesignationDisclaimer from "../../Linear/components/DesignationDisclaimer";
import { Tab } from "~/common/m3/Tabs/Tab";
import { Tabs } from "~/common/m3/Tabs/Tabs";
import { useDispatch, useSelector } from "react-redux";
import { XLIFE_LOGO_URL } from "../../Linear/constants";
import { useLinearPrice, userCanPurchaseLinear } from "../../Linear/components/Substeps/useLinearPrice";
import { Permission, userHasAnyPermission, userHasPermission } from "../../../utils/permissions";
import { userIsCustomer, userIsPublic, userIsRegistered } from "../../../utils/roles";
import NullableImage from "../../../common/components/NullableImage";
import ProductDesignation from "../../../ProductDetails/components/ProductDesignation";
import LanguageAwareExternalContent from "../../Linear/components/LanguageAwareExternalContent";
import GridGappedContainer, {
  GridGappedContainerDirection,
  GridGappedContainerSize,
} from "../../../common/components/GridGappedContainer/GridGappedContainer";
import { Card } from "../../Linear/components/Card";
import CircleSpinner from "../../../common/components/CircleSpinner/CircleSpinner";
import * as PriceUtils from "../../../utils/PriceUtils";
import { QuantityInput } from "../../../common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "../../../utils/constants";
import { setLCSummaryTabAction, updatePriceReference, updateQuantity } from "../../Linear/redux/actions";
import StockIndicator from "../../../common/components/StockIndicator/StockIndicator";

import { IconType } from "../../../common/components/icon/IconType";
import { ArrowLink } from "../../../common/components/Link";
import LinearATCPopup from "./Navigation/LinearATCPopup";
import LinearRequestQuotePopup from "../../Linear/components/LinearRequestQuotePopup";
import { LinearCad } from "../../Linear/components/LinearCad";
import { type RootState } from "../../../redux/store";
import { useSaveConfigPopup } from "../../Linear/hooks/useSaveConfigPopup";
import { CART_PAGE, REGISTER_PAGE, URL_CONTACT_US } from "../../../utils/url/staticUrls";
import { ConfiguratorType } from "../../ConfiguratorType";
import {
  getActiveTab,
  selectIsConfiguratorAddedToCartAlready,
  selectIsConfiguratorAddToCartEnabled,
} from "../../Linear/redux/selectors";
import { getRedirectToRegisterPage, redirectOrReload, redirectToUrl } from "../../../utils/url/urlUtils";
import { getNetPrice, makeDescriptorFromFullState } from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { useTrackLCSimpleButtonClick } from "../../Linear/hooks/useTrackLCClicks";
import { useRequestOfferPopup } from "../../Linear/hooks/useRequestOfferPopup";
import { PriceReferenceParam } from "../../../Cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "../../../QuickEntryPopup/helpers";
import { userCanSeeEndCustomer } from "../../../Cart/components/CartPage/LineItemStaticParams";
import { Button } from "~/common/components/Button/Button";
import { useTranslation } from "react-i18next";
import { useLinearAvailability } from "~/Configurators/Linear/components/Substeps/useLinearAvailability";
import { LcSummaryTab } from "~/Configurators/common/LcSummaryTab";
import { getConfiguratorDefinition } from "~/Configurators/configuratorRegistry";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { useLinearProductInfo } from "~/Configurators/Linear/redux/useLinearProductInfo";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Alert } from "~/common/m3/Alert/Alert";
import type { LinearSubstep } from "~/Configurators/Linear/models";

export const SCROLL_TO_LC_SUMMARY_TABS = () => document.getElementById("Tab")?.scrollIntoView();

export const ConfigurationSummarySubstep = ({ def }: { def: LinearSubstep }) => {
  const { t } = useTranslation();
  const xmlT = useXmlConfiguratorTranslations().t;
  const productCode = useSelector(selectConfiguratorProductCode);
  const activeTab = useSelector(getActiveTab);

  const SummaryData = getConfiguratorDefinition(productCode).summary;
  const SummaryParams = getConfiguratorDefinition(productCode).summaryParams;

  const dispatch = useDispatch();

  const updateActiveTab = useCallback((tab: LcSummaryTab) => dispatch(setLCSummaryTabAction(tab)), []);

  const linearConfiguration = useSelector(getLinearConfiguration);
  const quantity = useSelector(getQuantity);
  const manufacturerLogoUrl = useSelector(getManufacturerLogo);
  const brandLogoUrl = XLIFE_LOGO_URL;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isRequestQuoteOpen, setIsRequestQuoteOpen] = useState<boolean>(false);

  let imageUrl = useSelector(selectProductImageUrl);
  if (xmlT) {
    imageUrl = xmlT(imageUrl);
  }

  const openCloseModal = () => setIsModalOpen((prev) => !prev);
  const openCloseRequestQuote = () => setIsRequestQuoteOpen((prev) => !prev);

  const canPurchase = userCanPurchaseLinear();
  const canAddToCart = useSelector(selectIsConfiguratorAddToCartEnabled);
  const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);
  const canSeePrice = userHasPermission(Permission.PRICE);
  const canImportSave = userHasPermission(Permission.LINEAR_IMPORT);
  const isCustomer = userIsCustomer();
  const isRegistered = userIsRegistered();
  const isLatestKmatUser = userHasPermission(Permission.LINEAR_PURCHASE);
  const priceReference = useSelector(selectedPriceReference);

  const { priceInfo, errorLoadingPrice, isLoadingPrice } = useLinearPrice({
    priceReference: priceReference,
  });

  const { leadTime, isLoadingAvailability, errorLoadingAvailability } = useLinearAvailability();
  const canSeeAvailability = userHasPermission(Permission.AVAILABILITY);

  let leadTimeElement = null;
  if (canSeeAvailability) {
    if (isLoadingAvailability) {
      leadTimeElement = <CircleSpinner />;
    } else if (errorLoadingAvailability || !leadTime) {
      leadTimeElement = "-";
    } else {
      leadTimeElement = (
        <StockIndicator level={2} changeText={t("linearConfigSummary.card.avaliability", { count: leadTime })} />
      );
    }
  }

  const productInfo = useLinearProductInfo();
  const productDescriptor = makeDescriptorFromFullState(
    productInfo,
    { state: isLoadingPrice ? "loading" : "loaded", data: priceInfo ? [priceInfo] : [] },
    undefined,
    priceReference,
  );
  const price = getNetPrice({
    totalQuantity: quantity,
    packCode: "",
    product: productDescriptor,
  });

  const isPublicUser = userIsPublic();

  const trackCADTabClick = useTrackLCSimpleButtonClick("open_CAD_tab", {
    product_name: linearConfiguration.designation,
  });

  useEffect(() => {
    if (activeTab === LcSummaryTab.CAD) {
      SCROLL_TO_LC_SUMMARY_TABS();
    }
  }, []);

  const isAddedToCartAlready = useSelector(selectIsConfiguratorAddedToCartAlready);

  const valuesPriceReference = window.SCHA.config.priceReferences;
  const webservicesDisabled = window.SCHA.config.disableAllWS;
  if (userCanSeeEndCustomer()) {
    if (!priceReference && valuesPriceReference?.[1]?.value) {
      dispatch(updatePriceReference(valuesPriceReference[1].value));
    }
  }

  return (
    <>
      <div>
        <div className={"scha__pdp pt-2"}>
          <div className={"row mb-4"}>
            <div className={"col-4 pt-30"}>
              <div className="row d-flex">
                <div className="col-9">
                  <NullableImage
                    data={{
                      url: imageUrl,
                      altText: "linear configuration product",
                    }}
                  />
                </div>
                <div className="col-3 d-flex flex-direction-column flex-justify-between">
                  <div>
                    {manufacturerLogoUrl && (
                      <img className={"scha__pdp__manufacturer-logo"} src={manufacturerLogoUrl} />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={"col-push-1 col-3 pt-30"}>
              <ProductDesignation
                designation={linearConfiguration.designation}
                size={"headline-medium"}
                isPreferred={false}
              />
              <p className="scha__pdp__long-description">
                <LanguageAwareExternalContent
                  content={linearConfiguration.description}
                  loadedClassname={"scha__lc__external-label"}
                />
              </p>
              <GridGappedContainer
                className={"my-30"}
                direction={GridGappedContainerDirection.COLUMN}
                gapSize={GridGappedContainerSize.S_20}
              >
                {SummaryParams && <SummaryParams />}

                {(isPublicUser || isRegistered) && (
                  <>
                    <div>
                      <RequestOfferButton className={"scha__pdp__request-offer"} isPublicUser={isPublicUser}>
                        {t("linearConfigSummary.card.requestOffer")}
                      </RequestOfferButton>
                      <SaveConfigurationButton
                        className={"mb-3"}
                        isPublicUser={isPublicUser}
                        canImportSave={canImportSave}
                      >
                        {t("linearConfigSummary.card.saveConfig")}
                      </SaveConfigurationButton>
                      <ArrowLink href={URL_CONTACT_US}>{t("linearConfigSummary.card.contactSpecialist")}</ArrowLink>
                    </div>
                  </>
                )}
              </GridGappedContainer>
            </div>
            <div className={"col-4"}>
              {isCustomer && (
                <Card className={"label-large p-3"} disableHover={true}>
                  {canSeePrice && (
                    <div className={"mb-3 d-flex flex-justify-between"}>
                      <div>
                        <h2 className="scha__pdp__price-header">
                          {isLoadingPrice && <CircleSpinner />}
                          {!isLoadingPrice && !errorLoadingPrice && price && price.value > 0 && (
                            <>
                              <span>{PriceUtils.text(price)}</span>
                              <div className="label-large f-label-color f-normal">
                                {t("linearConfigSummary.pdp.perSystem")}
                              </div>
                            </>
                          )}
                          {!isLoadingPrice &&
                            !errorLoadingPrice &&
                            (price?.value ?? 0) === 0 &&
                            t("product.priceOnRequest")}
                          {!isLoadingPrice && errorLoadingPrice && !webservicesDisabled && (
                            <Alert severity={"error"}>{t("hybrisAjaxError")}</Alert>
                          )}
                        </h2>
                      </div>
                    </div>
                  )}

                  <PriceReferenceParam
                    product={productDescriptor.base}
                    className={"my-2"}
                    data-test-id={"pdp-price-reference"}
                    priceReference={priceReference}
                    editable={canSeePriceRefColumn()}
                    onChange={(value) => dispatch(updatePriceReference(value))}
                  />

                  <QuantityInput
                    min={0}
                    data-test-id={"pdp-input-quantity"}
                    max={MAX_QUANTITY}
                    value={quantity}
                    step={1}
                    onValueChange={(newVal) => dispatch(updateQuantity(newVal))}
                  />
                  <div className={"mb-2"}>
                    {isLatestKmatUser && (
                      <>
                        <div className={"d-flex flex-align-center mb-2"}>{leadTimeElement}</div>
                      </>
                    )}
                  </div>

                  {showAddToCart && !isAddedToCartAlready && (
                    <Button
                      className={"mb-3"}
                      startIcon={<DefaultButtonIcon type={IconType.Cart} />}
                      data-test-id={"linear-config-summary-add-to-cart-btn"}
                      fullWidth={true}
                      disabled={!canAddToCart || webservicesDisabled}
                      onClick={canPurchase ? openCloseModal : openCloseRequestQuote}
                    >
                      {t(`linearConfigSummary.card.${canPurchase ? "addToCart" : "addToQuote"}`)}
                    </Button>
                  )}

                  {showAddToCart && isAddedToCartAlready && (
                    <Button
                      className={"mb-3"}
                      startIcon={<DefaultButtonIcon type={IconType.Cart} />}
                      data-test-id={"linear-config-summary-add-to-cart-btn"}
                      fullWidth={true}
                      href={CART_PAGE}
                      disabled={webservicesDisabled}
                    >
                      {t(`page.linear.addToCart.button.goToCart`)}
                    </Button>
                  )}
                  <ArrowLink href={URL_CONTACT_US}>{t("linearConfigSummary.card.contactSpecialist")}</ArrowLink>
                </Card>
              )}
            </div>
            {isModalOpen && <LinearATCPopup onClose={openCloseModal} />}
            {isRequestQuoteOpen && <LinearRequestQuotePopup onClose={openCloseRequestQuote} />}
          </div>
        </div>
      </div>

      <DesignationDisclaimer />

      <Tabs
        id={"Tab"}
        fullWidthIndicator={false}
        variant={"fullWidth"}
        value={activeTab}
        onChange={(e, v) => {
          const val = v as LcSummaryTab;
          if (val === LcSummaryTab.CAD) {
            if (isPublicUser) {
              redirectToUrl(getRedirectToRegisterPage({ openLogin: true }));
              return;
            } else {
              trackCADTabClick();
              updateActiveTab(val);
            }
          } else {
            updateActiveTab(val);
          }
        }}
      >
        <Tab
          data-test-id={"linear-tab-information"}
          value={LcSummaryTab.ProductInformation}
          label={t("linearConfigSummary.tab.productInformation")}
        />
        <Tab data-test-id={"linear-tab-cad"} value={LcSummaryTab.CAD} label={t("linearConfigSummary.tab.cad.label")} />
      </Tabs>
      <LazyRenderPanel visible={activeTab === LcSummaryTab.ProductInformation}>
        <SummaryData isFullReview={true} />
      </LazyRenderPanel>
      <LazyRenderPanel visible={activeTab === LcSummaryTab.CAD}>
        <LinearCad />
      </LazyRenderPanel>
    </>
  );
};

const LazyRenderPanel = ({ visible, children }: { visible: boolean; children: ReactNode }) => {
  const [renderedOnce, setRenderedOnce] = useState(visible);
  useLayoutEffect(() => {
    if (visible && !renderedOnce) {
      setRenderedOnce(true);
    }
  }, [visible]);

  if (renderedOnce) {
    return (
      <div className={"scha__lc__summary--full-page"} hidden={!visible}>
        {children}
      </div>
    );
  }
  return null;
};

const getLinearConfiguration = (state: RootState) => state.linearConfigurator;
export const getSystemLayout = (state: RootState) => state.linearConfigurator.substeps.SystemLayout;
export const selectProductImageUrl = (state: RootState) => {
  if (state.linearConfigurator.type === ConfiguratorType.System) {
    const systemLayout = getSystemLayout(state);
    const tileSelectedInFirstStep = systemLayout.selectedTile;
    return systemLayout.tiles[tileSelectedInFirstStep!].imageUrl;
  }

  return state.linearConfigurator.productImageUrl;
};
const getQuantity = (state: RootState) => state.linearConfigurator.quantity;
const getManufacturerLogo = (state: RootState) => state.linearConfigurator.manufacturerLogo;

const RequestOfferButton: FC<{
  className?: string;
  isPublicUser: boolean;
  children?: ReactNode;
}> = ({ children, className, isPublicUser }) => {
  const { openModal, component } = useRequestOfferPopup();
  const trackClick = useTrackLCSimpleButtonClick("request_offer");

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackClick();
    if (!isPublicUser) {
      openModal();
    } else {
      redirectOrReload(REGISTER_PAGE);
    }
  };

  return (
    <>
      <Button
        className={className}
        href={isPublicUser ? REGISTER_PAGE : undefined}
        data-test-id={"linear-config-summary-request-offer-button"}
        fullWidth={true}
        onClick={onClick}
      >
        {children}
      </Button>

      {component}
    </>
  );
};

const SaveConfigurationButton: FC<{
  className?: string;
  isPublicUser: boolean;
  canImportSave: boolean;
  children?: ReactNode;
}> = ({ children, className, isPublicUser, canImportSave }) => {
  const { openModal, component } = useSaveConfigPopup();

  const onClick = () => {
    openModal();
  };

  return (
    <>
      <Button
        color={"secondary"}
        className={className}
        href={isPublicUser ? REGISTER_PAGE : undefined}
        data-test-id={"linear-config-summary-save-configuration-button"}
        fullWidth={true}
        onClick={!isPublicUser && canImportSave ? onClick : undefined}
      >
        {children}
      </Button>

      {component}
    </>
  );
};

export const selectDataComponent = (state: RootState) => {
  const productCode = state.linearConfigurator.productCode;
  return getConfiguratorDefinition(productCode).summary;
};

const selectConfiguratorType = (state: RootState) => {
  return state.linearConfigurator.type;
};
const selectConfiguratorProductCode = (state: RootState) => {
  return state.linearConfigurator.productCode;
};

export const selectedPriceReference = (state: RootState) => state.linearConfigurator.priceReference;
